import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import TicketDialog from "@/components/TicketDialog";
import { Badge } from "@/components/ui/badge";
import { KanbanSquare, Calendar, CheckSquare, MessageSquare, CheckCircle2 } from "lucide-react";

const PRIO_CLR = { low: "bg-slate-400", medium: "bg-blue-500", high: "bg-amber-500", urgent: "bg-red-500" };

export default function MyTickets() {
  const { t } = useT();
  const [tickets, setTickets] = useState([]);
  const [columns, setColumns] = useState([]);
  const [meta, setMeta] = useState({ clients: [], users: [] });
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    api.get("/tickets/mine").then((r) => { setTickets(r.data.tickets); setColumns(r.data.columns); });
  }, []);
  useEffect(() => { load(); api.get("/tickets/meta").then((r) => setMeta(r.data)); }, [load]);

  const colName = (id) => columns.find((c) => c.id === id)?.name || "—";
  const colColor = (id) => columns.find((c) => c.id === id)?.color || "#64748B";
  const cname = (id) => meta.clients.find((c) => c.id === id)?.name;

  const active = tickets.filter((tk) => tk.status !== "done");
  const done = tickets.filter((tk) => tk.status === "done");

  const Card = ({ tk }) => (
    <button onClick={() => setOpenId(tk.id)} data-testid="my-ticket-card"
      className={`w-full text-left rounded-xl bg-card border border-border p-4 hover:border-primary/50 transition-colors ${tk.status === "done" ? "opacity-70" : ""}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="font-mono text-[10px] text-muted-foreground">{tk.number}</span>
        <span className={`w-2 h-2 rounded-full ${PRIO_CLR[tk.priority]}`} title={tk.priority} />
        <span className="text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-1" style={{ backgroundColor: `${colColor(tk.column_id)}22`, color: colColor(tk.column_id) }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colColor(tk.column_id) }} />{colName(tk.column_id)}
        </span>
        {tk.status === "done" && <Badge className="bg-emerald-500 text-[9px] ml-auto"><CheckCircle2 className="w-3 h-3 mr-0.5" />{t("statusDone")}</Badge>}
      </div>
      <p className="text-sm font-medium leading-snug">{tk.title}</p>
      {tk.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{tk.description}</p>}
      <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground flex-wrap">
        {tk.ri_client_id && <span className="truncate max-w-[160px]">{cname(tk.ri_client_id)}</span>}
        {tk.due_date && <span className="flex items-center gap-0.5"><Calendar className="w-3 h-3" />{tk.due_date.slice(5)}</span>}
        {tk.checklist?.length > 0 && <span className="flex items-center gap-0.5"><CheckSquare className="w-3 h-3" />{tk.checklist.filter((c) => c.done).length}/{tk.checklist.length}</span>}
        {tk.labels?.includes("email") && <Badge variant="outline" className="text-[9px]"><MessageSquare className="w-2.5 h-2.5 mr-0.5" />Email</Badge>}
      </div>
    </button>
  );

  return (
    <div className="h-full overflow-y-auto scrollbar-thin">
      <div className="px-4 sm:px-6 py-4 border-b border-border">
        <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><KanbanSquare className="w-6 h-6 text-primary" />{t("myTickets")}</h1>
        <p className="text-sm text-muted-foreground mt-0.5">{t("myTicketsSubtitle")}</p>
      </div>

      <div className="p-4 sm:p-6 max-w-3xl space-y-3">
        {tickets.length === 0 && (
          <div className="flex flex-col items-center justify-center text-muted-foreground gap-2 py-16 text-center">
            <KanbanSquare className="w-10 h-10" />
            <p className="text-sm">{t("noAssignedTickets")}</p>
          </div>
        )}
        {active.map((tk) => <Card key={tk.id} tk={tk} />)}
        {done.length > 0 && (
          <>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-4">{t("statusDone")}</p>
            {done.map((tk) => <Card key={tk.id} tk={tk} />)}
          </>
        )}
      </div>

      {openId && <TicketDialog ticketId={openId} meta={meta} open={!!openId} canEdit={false} canComplete
        onOpenChange={(o) => { if (!o) setOpenId(null); }} onChanged={load} />}
    </div>
  );
}
